export type DateSort = 'asc' | 'desc'
export type LedgerSortField =
  | 'date'
  | 'employee'
  | 'category'
  | 'itemCategory'
  | 'amount'
  | 'employer'
  | 'employeeAmount'

export type DatedAdvanceRow = {
  id: string
  date: string | null
  scheduled?: boolean
  itemCategory?: string
  employeeName?: string
  employeeId?: string
  category?: string
  categoryLabel?: string
  amount?: number
  employerContribution?: number
  employeeAmount?: number
}

export function compareAdvanceRowsChronological(a: DatedAdvanceRow, b: DatedAdvanceRow) {
  const dt = String(a.date || '').localeCompare(String(b.date || ''))
  if (dt !== 0) return dt
  if (!!a.scheduled !== !!b.scheduled) return a.scheduled ? 1 : -1
  return String(a.id).localeCompare(String(b.id))
}

function textKey(value: string | undefined | null) {
  return String(value || '').trim()
}

function employeeKey(row: DatedAdvanceRow) {
  return textKey(row.employeeName) || textKey(row.employeeId)
}

function categoryKey(row: DatedAdvanceRow) {
  return textKey(row.categoryLabel) || textKey(row.category)
}

function itemCategoryKey(row: DatedAdvanceRow) {
  return textKey(row.itemCategory)
}

function compareText(a: string, b: string, dir: number, emptyLast = false) {
  if (emptyLast) {
    const empty = Number(!a) - Number(!b)
    if (empty !== 0) return empty
  }
  const cmp = a.localeCompare(b, undefined, { sensitivity: 'base' })
  if (cmp !== 0) return cmp * dir
  return 0
}

function compareNumber(a: number, b: number, dir: number) {
  if (a === b) return 0
  return (a - b) * dir
}

export function sortAdvanceRows<T extends DatedAdvanceRow>(
  rows: T[],
  dateSort: DateSort,
  sortField: LedgerSortField = 'date',
): T[] {
  const dir = dateSort === 'asc' ? 1 : -1
  if (sortField === 'date') {
    const chronological = [...rows].sort(compareAdvanceRowsChronological)
    return dateSort === 'asc' ? chronological : chronological.slice().reverse()
  }
  return [...rows].sort((a, b) => {
    let cmp = 0
    if (sortField === 'employee') {
      cmp = compareText(employeeKey(a), employeeKey(b), dir)
      if (cmp === 0) cmp = compareText(textKey(a.employeeId), textKey(b.employeeId), dir)
    } else if (sortField === 'category') {
      cmp = compareText(categoryKey(a), categoryKey(b), dir)
    } else if (sortField === 'itemCategory') {
      cmp = compareText(itemCategoryKey(a), itemCategoryKey(b), dir, true)
    } else if (sortField === 'amount') {
      cmp = compareNumber(Number(a.amount || 0), Number(b.amount || 0), dir)
    } else if (sortField === 'employer') {
      cmp = compareNumber(Number(a.employerContribution || 0), Number(b.employerContribution || 0), dir)
    } else if (sortField === 'employeeAmount') {
      cmp = compareNumber(Number(a.employeeAmount || 0), Number(b.employeeAmount || 0), dir)
    }
    if (cmp !== 0) return cmp
    return compareAdvanceRowsChronological(a, b)
  })
}
